"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const Alert_1 = __importDefault(require("@material-ui/lab/Alert"));
const AlertTitle_1 = __importDefault(require("@material-ui/lab/AlertTitle"));
const CheckCircleOutline_1 = __importDefault(require("@material-ui/icons/CheckCircleOutline"));
const ReportProblemOutlined_1 = __importDefault(require("@material-ui/icons/ReportProblemOutlined"));
const CancelOutlined_1 = __importDefault(require("@material-ui/icons/CancelOutlined"));
const InfoOutlined_1 = __importDefault(require("@material-ui/icons/InfoOutlined"));
const Close_1 = __importDefault(require("@material-ui/icons/Close"));
const IconButton_1 = __importDefault(require("@material-ui/core/IconButton"));
const core_1 = require("@material-ui/core");
const clsx_1 = __importDefault(require("clsx"));
const Button_1 = __importDefault(require("../Button"));
const Box_1 = __importDefault(require("../../layouts/Box"));
const useStyles = core_1.makeStyles((theme) => ({
    muiAlertMessageOverride: {
        justifyContent: 'flex-start',
        whiteSpace: 'pre-wrap',
    },
    childrenContainer: {
        flexDirection: 'row',
        wordWrap: 'break-word',
    },
    noBorderRadius: {
        borderRadius: 0,
    },
}));
const iconMapping = {
    success: react_1.default.createElement(CheckCircleOutline_1.default, { fontSize: "inherit", titleAccess: "success", "aria-label": "success" }),
    warning: react_1.default.createElement(ReportProblemOutlined_1.default, { fontSize: "inherit", titleAccess: "warning", "aria-label": "warning" }),
    error: react_1.default.createElement(CancelOutlined_1.default, { fontSize: "inherit", titleAccess: "error", "aria-label": "error" }),
    info: react_1.default.createElement(InfoOutlined_1.default, { fontSize: "inherit", titleAccess: "info", "aria-label": "info" }),
};
/**
    An alert helps the user know when they need to pay attention to a relatively small piece of information or when they need to take a special action.
**/
const Alert = (_a) => {
    var { visible = true, onDismiss, onButtonClick, dismissAriaLabel = 'Close', buttonText = '', borderRadius = true } = _a, props = __rest(_a, ["visible", "onDismiss", "onButtonClick", "dismissAriaLabel", "buttonText", "borderRadius"]);
    const styles = useStyles({});
    const [show, setShow] = react_1.default.useState(true);
    const handleButtonClick = react_1.useCallback((event) => {
        event.preventDefault();
        onButtonClick === null || onButtonClick === void 0 ? void 0 : onButtonClick();
    }, [onButtonClick]);
    const actionButton = react_1.useMemo(() => react_1.default.createElement(react_1.default.Fragment, null, buttonText && react_1.default.createElement(Button_1.default, { onClick: handleButtonClick }, buttonText)), [buttonText, handleButtonClick]);
    const closeButton = react_1.useMemo(() => (react_1.default.createElement(react_1.default.Fragment, null,
        actionButton,
        show && (react_1.default.createElement(IconButton_1.default, { "aria-label": dismissAriaLabel, onClick: () => {
                setShow(false);
                onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
            } },
            react_1.default.createElement(Close_1.default, null))))), [actionButton, dismissAriaLabel, show, onDismiss, setShow]);
    const mapProps = react_1.useCallback((alertType, { type = 'info' }) => {
        return {
            severity: type,
            iconMapping,
            action: alertType === 'dismissible' ? closeButton : actionButton,
        };
    }, [closeButton, actionButton]);
    const renderAlertBody = react_1.useCallback(({ header, children }, materialProps) => (react_1.default.createElement(Alert_1.default, Object.assign({ variant: "outlined" }, materialProps, { classes: {
            message: styles.muiAlertMessageOverride,
            root: clsx_1.default({ [styles.noBorderRadius]: !borderRadius }),
        } }),
        header && (react_1.default.createElement("div", { role: "heading", "aria-level": 1 },
            react_1.default.createElement(AlertTitle_1.default, null, header))),
        react_1.default.createElement("div", { className: styles.childrenContainer }, children))), [styles, borderRadius]);
    const renderAlert = react_1.useCallback((_a) => {
        var { dismissible } = _a, props = __rest(_a, ["dismissible"]);
        return renderAlertBody(props, mapProps(dismissible ? 'dismissible' : 'pinned', props));
    }, [renderAlertBody, mapProps]);
    return (react_1.default.createElement(Box_1.default, { "data-testid": props.type, width: "100%" }, visible && show && renderAlert(props)));
};
exports.default = Alert;
//# sourceMappingURL=index.js.map